import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "@tanstack/react-router";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useDialogSearchParam } from "@/hooks/use-dialog-search-param";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import { useSizeClass } from "@/hooks/use-size-class";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { PageTitle } from "@/contexts/page-title-context";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { Card } from "@/components/ui/card";
import { Icon } from "@/components/ui/icons";
import { IconCircle } from "@/components/ui/icon-circle";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from "@/components/ui/table";
import { IconButton } from "@/components/ui/icon-button";
import { Skeleton } from "@/components/ui/skeleton";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { HStack } from "@/components/ui/h-stack";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { DividedList } from "@/components/ui/divided-list";
import { useUserEntity } from "@/contexts/user-entity-context";
import { getContacts, deleteContact, type Contact } from "@/lib/api/contacts";
import { highlight } from "@/lib/highlight";
import { contactSearchableFields } from "@/lib/search/contact";
import { getBrokerShippers, type BrokerParty } from "@/lib/api/broker-parties";
import { getCurrentUser } from "@/lib/api/auth";
import { ContactModal } from "@/components/forms/contact-modal";
import { useDeleteWithConfirm } from "@/hooks/use-delete-with-confirm";

// Helper to get first contact by type (for table display)

function ContactsPage() {
  const { t: tContacts } = useTranslation("contacts");
  const { t: tCommon } = useTranslation("common");
  const { t: tCommonPagination } = useTranslation("common.pagination");
  const { t: tLoads } = useTranslation("loads");
  const searchParams = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const { entity: userEntity } = useUserEntity();
  const isBroker = userEntity?.type === "Broker";
  const shipperParam = searchParams.shipper_id ?? "";
  const { dialog, openDialog, closeDialog } = useDialogSearchParam();
  const { filters, setFilter } = useFilterSearchParams({ keys: ["search"] });
  const searchQuery = filters.search;
  const [brokerShippers, setBrokerShippers] = useState<BrokerParty[]>([]);
  const [selectedShipperId, setSelectedShipperId] = useState("");
  const [isShipperLocked, setIsShipperLocked] = useState(false);

  const { offset } = usePageOffset();
  const shipperId =
    isBroker && selectedShipperId ? selectedShipperId : undefined;
  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);
  const {
    data,
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      ...queryKeys.contacts({ limit: DEFAULT_PAGE_SIZE, offset }),
      shipperId,
      q,
    ],
    queryFn: () =>
      getContacts({
        limit: DEFAULT_PAGE_SIZE,
        offset,
        shipper_id: shipperId,
        q,
      }),
  });
  const contacts = data?.data ?? [];
  const total = data?.total ?? 0;
  const { page, totalPages, setPage } = usePagination({ total });

  const {
    showConfirm: showDeleteConfirm,
    loading: deleteLoading,
    requestDelete,
    confirmDelete,
    cancelDelete,
  } = useDeleteWithConfirm({
    deleteFn: deleteContact,
    onSuccess: () => void refetch(),
    successMessage: tCommon("deleteSuccess"),
    errorMessage: tCommon("deleteFailed"),
  });

  const selectedBrokerShipper = useMemo(
    () => brokerShippers.find((shipper) => shipper.id === selectedShipperId),
    [brokerShippers, selectedShipperId],
  );

  useEffect(() => {
    if (!isBroker) return;

    getBrokerShippers({ limit: 100 })
      .then((response) => setBrokerShippers(response.data))
      .catch(console.error);
  }, [isBroker]);

  useEffect(() => {
    if (!isBroker) return;

    if (shipperParam) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedShipperId(shipperParam);
      setIsShipperLocked(true);
    } else {
      setIsShipperLocked(false);
    }
  }, [isBroker, shipperParam]);

  // Search narrows server-side via ?q= — no client filter.
  const filteredContacts = contacts;

  const handleEdit = (contact: Contact) => {
    openDialog("contact", contact.id);
  };

  // Handle add new
  const handleAddNew = () => {
    if (!isShipperLocked) {
      setSelectedShipperId("");
    }
    openDialog("contact");
  };

  // Close modal
  const closeModal = () => {
    closeDialog();
  };

  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";

  // ─── Resizable columns ──────────────────────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const columnDefs: ColumnDef[] = [
    { key: "contact", defaultWidth: 200, minWidth: 100 },
    { key: "phoneEmail", defaultWidth: 250, minWidth: 120 },
    { key: "actions", defaultWidth: 180, minWidth: 100 },
  ];

  return (
    <VStack gap="lg" data-testid="contacts-page">
      {/* Page header */}
      {/* contacts-header */}
      <PageTitle>{tContacts("title")}</PageTitle>
      <StickyHeaderSlot>
        <Card
          data-testid="contacts-header-actions-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            data-testid="contacts-header-actions"
            searchPlaceholder={tContacts("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            createAction={{
              label: tContacts("addContact"),
              onClick: handleAddNew,
              testId: "contacts-add-btn",
            }}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
          />
        </Card>
      </StickyHeaderSlot>

      {/* Content */}
      <Card data-testid="contacts-content-card" padding="none">
        <TableViewport position="relative">
          {/* Loading state */}
          {loading && contacts.length === 0 && (
            <Table data-testid="contacts-table-skeleton">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{tCommon("contact")}</TableHeaderCell>
                  <TableHeaderCell>
                    {tContacts("phone")} / {tContacts("email")}
                  </TableHeaderCell>
                  <TableHeaderCell>{tLoads("actions")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <HStack gap="lg">
                        <Skeleton rounded="full" className="h-9 w-9" />
                        <Skeleton className="h-5 w-32" />
                      </HStack>
                    </TableCell>
                    <TableCell>
                      <Skeleton className="mb-2 h-4 w-32" />
                      <Skeleton className="h-3 w-40" />
                    </TableCell>
                    <TableCell>
                      <HStack align="stretch">
                        <Skeleton rounded="full" className="h-8 w-8" />
                        <Skeleton rounded="full" className="h-8 w-8" />
                      </HStack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Error state */}
          {isError && !loading && (
            <ErrorState
              title={tCommon("errorLoadingData")}
              action={
                <Button
                  color="neutral"
                  variant="outline"
                  onClick={() => void refetch()}
                >
                  {tCommon("retry")}
                </Button>
              }
              className="py-16"
            />
          )}

          {/* Table or Empty state */}
          {!loading && !isError && filteredContacts.length === 0 && (
            <EmptyState
              title={tContacts("noContactsTitle")}
              description={tContacts("noContactsDescription")}
              icon={<Icon.Contacts size="xl" />}
              action={
                !isCompact ? (
                  <Button onClick={handleAddNew} size="md">
                    <Icon.Plus />
                    {tContacts("addContact")}
                  </Button>
                ) : undefined
              }
              className="py-16"
            />
          )}

          {loading && contacts.length > 0 && (
            <PageLoadingOverlay
              page={page}
              label={tCommonPagination("loadingPage")}
            />
          )}

          {!loading &&
            !isError &&
            filteredContacts.length > 0 &&
            (isCompact ? (
              <DividedList data-testid="contacts-card-list">
                {filteredContacts.map((contact) => {
                  const fields = contactSearchableFields(contact);
                  const phone = fields.phone;
                  const email = fields.email;
                  return (
                    <HStack
                      key={contact.id}
                      gap="lg"
                      className="px-4 py-3"
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <IconCircle color="muted" className="w-9 h-9">
                        <Icon.User size="sm" />
                      </IconCircle>
                      <VStack fill truncate gap="none">
                        <Text weight="semibold" truncate>
                          {highlight(fields.name, searchQuery)}
                        </Text>
                        {phone.value && (
                          <HStack gap="sm">
                            <Icon.Phone size="xs" color="muted" />
                            <Text size="xs" color="muted" truncate>
                              {highlight(phone, searchQuery)}
                            </Text>
                          </HStack>
                        )}
                        {email.value && (
                          <HStack gap="sm">
                            <Icon.Email size="xs" color="muted" />
                            <Text size="xs" color="muted" truncate>
                              {highlight(email, searchQuery)}
                            </Text>
                          </HStack>
                        )}
                      </VStack>
                      <HStack gap="xs" shrink>
                        <IconButton
                          type="button"
                          data-testid={`contact-edit-${contact.id}`}
                          className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--primary)]"
                          title={tCommon("edit")}
                          onClick={() => handleEdit(contact)}
                        >
                          <Icon.Edit size="sm" />
                        </IconButton>
                        <IconButton
                          type="button"
                          data-testid={`contact-delete-${contact.id}`}
                          className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--error)]"
                          title={tCommon("delete")}
                          onClick={() => requestDelete(contact.id)}
                        >
                          <Icon.Trash size="sm" />
                        </IconButton>
                      </HStack>
                    </HStack>
                  );
                })}
              </DividedList>
            ) : (
              <ResizableTableShell
                tableId="contacts"
                userId={userId}
                columns={columnDefs}
                data-testid="contacts-table"
              >
                {({ onResizeStart }) => (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell className="relative">
                          {tCommon("contact")}
                          <ResizeHandle
                            columnKey="contact"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="relative">
                          {tContacts("phone")} / {tContacts("email")}
                          <ResizeHandle
                            columnKey="phoneEmail"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="relative" sticky>
                          {tLoads("actions")}
                          <ResizeHandle
                            columnKey="actions"
                            onResizeStart={onResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="w-auto" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredContacts.map((contact) => {
                        const fields = contactSearchableFields(contact);
                        const phone = fields.phone;
                        const email = fields.email;

                        return (
                          <TableRow
                            key={contact.id}
                            hoverable
                            data-testid={`contact-row-${contact.id}`}
                          >
                            <TableCell>
                              <HStack gap="lg">
                                <IconCircle color="muted" className="w-9 h-9">
                                  <Icon.User size="sm" />
                                </IconCircle>
                                <Text weight="semibold">
                                  {highlight(fields.name, searchQuery)}
                                </Text>
                              </HStack>
                            </TableCell>
                            <TableCell>
                              <VStack gap="xs">
                                {phone.value && (
                                  <HStack gap="sm">
                                    <Icon.Phone size="xs" color="muted" />
                                    <Text>{highlight(phone, searchQuery)}</Text>
                                  </HStack>
                                )}
                                {email.value && (
                                  <HStack gap="sm">
                                    <Icon.Email size="xs" color="muted" />
                                    <Text size="xs" color="muted">
                                      {highlight(email, searchQuery)}
                                    </Text>
                                  </HStack>
                                )}
                              </VStack>
                            </TableCell>
                            <TableCell sticky>
                              <HStack>
                                <IconButton
                                  type="button"
                                  data-testid={`contact-edit-${contact.id}`}
                                  className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--primary)]"
                                  title={tCommon("edit")}
                                  onClick={() => handleEdit(contact)}
                                >
                                  <Icon.Edit size="sm" />
                                </IconButton>
                                <IconButton
                                  type="button"
                                  data-testid={`contact-delete-${contact.id}`}
                                  className="text-[color:var(--on-surface-variant)] hover:text-[color:var(--error)]"
                                  title={tCommon("delete")}
                                  onClick={() => requestDelete(contact.id)}
                                >
                                  <Icon.Trash size="sm" />
                                </IconButton>
                              </HStack>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </>
                )}
              </ResizableTableShell>
            ))}
        </TableViewport>
      </Card>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={tCommon("confirmDelete")}
        description={tCommon("confirmDeleteDescription")}
        confirmLabel={tCommon("delete")}
        cancelLabel={tCommon("cancel")}
        confirmColor="danger"
        loading={deleteLoading}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />

      <ContactModal
        open={dialog?.type === "contact"}
        onClose={closeModal}
        contactId={dialog?.id}
        onSaved={() => {
          void refetch();
          closeModal();
        }}
        shipperParty={selectedBrokerShipper ?? null}
        shipperPartyEditable={Boolean(isBroker && !dialog?.id)}
        shipperPartyOptions={brokerShippers.map((shipper) => ({
          value: shipper.id,
          label: shipper.name,
        }))}
        onShipperPartyChange={setSelectedShipperId}
        shipperPartyDisabled={isShipperLocked}
      />

      {isCompact && (
        <Fab
          onClick={handleAddNew}
          aria-label={tContacts("addContact")}
          data-testid="contacts-fab"
        />
      )}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/contacts")({
  component: ContactsPage,
});
